import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useCountries from '@afosto/utils-react/useCountries';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Filter from '@afosto/app-components-react/Filter';
import Property from '@afosto/app-components-react/Property';
import formatCurrency from '@afosto/utils/formatCurrency';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import { services as servicesApi } from '@afosto/order/api/Services';
import { vat as vatApi } from '@afosto/catalog/api/Vat';
import FiltersRenderer from '../../../components/FiltersRenderer';
import constraintsRenderFormats from '../../../data/constraintsRenderFormats';
import filtersRenderFormats from '../../../data/filtersRenderFormats';
import translations from './translations';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';

const ServiceCostsPreview = ({
  service,
  isOpen,
  isLoading,
  previewMenu,
  onClose,
  onDeactivateService,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const countries = useCountries();
  const [vatRates, setVatRates] = useState([]);

  const handleDeactivateService = async () => {
    try {
      const response = await servicesApi.updateService(service.id, {
        ...service,
        expires_at: new Date(),
      });
      const { data } = response;

      enqueueSnackbar(intl.formatMessage(translations.feedback.serviceCostDeactivated), {
        variant: 'success',
      });

      if (onDeactivateService) {
        await onDeactivateService(data);
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.serviceCostNotDeactivated), {
        variant: 'error',
      });
    }
  };

  const checkExpiredDate = () => {
    const currentDate = new Date();
    const expiredDate = new Date(service.expires_at);

    return currentDate.getTime() > expiredDate.getTime();
  };

  const isExpired = service?.expires_at ? checkExpiredDate() : false;

  const menu = [
    ...(previewMenu || []),
    ...(service
      ? [
          {
            key: 'edit',
            label: intl.formatMessage(translations.actions.edit),
            icon: 'pencil',
            shortcut: true,
            onClick: () => history.push(`/service-costs/edit/${service.id}`),
          },
        ]
      : []),
    ...(service && !isExpired
      ? [
          {
            key: 'deactivate',
            label: intl.formatMessage(translations.actions.deactivate),
            icon: 'ban',
            color: 'secondary',
            onClick: handleDeactivateService,
          },
        ]
      : []),
  ];

  useEffect(() => {
    let isMounted = true;

    const fetchAllVatRates = async () => {
      try {
        const response = await vatApi.getVatList();
        const { data: vatRateList = [] } = response;

        if (isMounted) {
          setVatRates(
            vatRateList.map(vatRate => ({
              ...vatRate,
              label: `${vatRate.rate}%`,
              value: vatRate.reference,
            })),
          );
        }
      } catch (err) {
        if (isMounted) {
          setVatRates([]);
        }
      }
    };

    fetchAllVatRates();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <DataGridPreview title={service?.name ?? ''} menu={menu} open={isOpen} onClose={onClose}>
      <LoadingWrapper isLoading={isLoading} style={{ minHeight: 200 }}>
        <Box px={2} pb={3}>
          <Property
            label={intl.formatMessage(translations.properties.name)}
            value={service?.name}
          />

          <Property
            label={intl.formatMessage(translations.properties.type)}
            value={intl.formatMessage({
              id: `serviceCostForm.types.${service?.type}`,
              defaultMessage: service?.type,
              description: `Label of the service type ${service?.type}`,
            })}
          />

          <Property
            label={intl.formatMessage(translations.properties.value)}
            value={
              service?.is_percentage ? `${service?.amount}%` : formatCurrency(service?.amount / 100)
            }
          />

          <Property
            label={intl.formatMessage(translations.properties.priority)}
            value={service?.priority}
          />

          <Property
            label={intl.formatMessage(translations.properties.activeAt)}
            value={service?.active_at ? formatDateTime(service.active_at) : null}
          />

          <Property
            label={intl.formatMessage(translations.properties.expiresAt)}
            value={service?.expires_at ? formatDateTime(service.expires_at) : null}
          />

          {service?.vat_settings?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.vatSettings} />
                </Typography>
              </Box>

              {(service?.vat_settings ?? []).map(vat => {
                const vatRate = findObjectInArray(vatRates, 'value', vat.reference);
                return (
                  <>
                    <Property
                      label={intl.formatMessage(translations.properties.type)}
                      value={intl.formatMessage({
                        id: `serviceCostForm.vatTypes.${vat?.type}`,
                        defaultMessage: vat?.type,
                        description: `Label of the service type ${vat?.type}`,
                      })}
                    />
                    <Property
                      label={intl.formatMessage(translations.properties.countryCode)}
                      value={
                        findObjectInArray(countries, 'value', vat.country_code)?.label ??
                        vat?.country_code
                      }
                    />
                    <Property
                      label={intl.formatMessage(translations.properties.rate)}
                      value={vatRate?.rate ? `${vatRate?.rate}%` : vat?.reference}
                    />
                  </>
                );
              })}
            </>
          )}

          {service?.constraints?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.constraints} />
                </Typography>
              </Box>

              {(service?.constraints ?? []).map(constraint => (
                <Filter
                  key={constraint?.key}
                  filter={{ ...constraint, type: constraintsRenderFormats[constraint.key] }}
                  translationKey="serviceCostForm.constraintTypes"
                />
              ))}
            </>
          )}

          {service?.filters?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.conditions} />
                </Typography>
              </Box>

              {(service?.filters ?? []).map(filter => (
                <Filter
                  key={filter?.key}
                  filter={{ ...filter, type: filtersRenderFormats[filter.key] }}
                  translationKey="pricingRuleForm.filterTypes"
                  renderers={{
                    filters: FiltersRenderer,
                  }}
                />
              ))}
            </>
          )}
        </Box>
      </LoadingWrapper>
    </DataGridPreview>
  );
};

ServiceCostsPreview.propTypes = {
  service: PropTypes.object,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  previewMenu: PropTypes.array,
  onClose: PropTypes.func,
  onDeactivateService: PropTypes.func,
};

ServiceCostsPreview.defaultProps = {
  service: null,
  isOpen: false,
  isLoading: false,
  previewMenu: [],
  onClose: undefined,
  onDeactivateService: undefined,
};

export default ServiceCostsPreview;
